import { html, SP } from '../../../html.js';
import type { VNode } from '../../../html.js';
import type { TenantDetail } from '../../api.js';
import type { DashboardActions, DashboardState } from '../../store.js';
import { Empty } from '../../ui/bits.js';
import { CapabilityCard, isRegistered } from '../../ui/capcard.js';

/**
 * Tab two: what this site advertises, and whether each of them works.
 *
 * The card is unchanged. It is doing six jobs — identity, issues, description
 * editing, surfaces, schemas, the live runner and the typed-confirm delete —
 * and the plan for this area splits it across a card and a `#/sites/:id/tools/
 * :name` detail route so a broken tool has a URL somebody can paste into a
 * support message. That split is the next phase and it is not this one: moving
 * the card here first is what makes it possible to do without also moving the
 * rest of the tenant screen at the same time.
 *
 * WHAT IS NEW IS THE SECOND LIST. The crawl now ships a tool it scored below
 * its usefulness threshold with both surfaces switched off rather than deleting
 * it, so a manifest of twelve capabilities can serve three — and until this
 * split existed, the other nine sat in the same list looking live. They are
 * separated because they are a different question: the first list is "does this
 * work", the second is "did the crawl judge this correctly", and only the
 * second one has a switch as its answer.
 *
 * A site with no tools is a real published state, not a failure: the tenant
 * still serves a branded page and an MCP endpoint, and the crawl is where more
 * tools come from, so that is where the empty state points.
 */
export function ToolsTab(props: {
  t: TenantDetail;
  state: DashboardState;
  actions: DashboardActions;
  now: number;
}): VNode {
  const { t, state, actions, now } = props;
  const callsByTool = new Map(t.usage.byTool.map((a) => [a.tool, a]));
  const healthByTool = new Map(t.health.tools.map((h) => [h.tool, h]));
  const served = t.capabilities.filter((c) => isRegistered(c));
  const off = t.capabilities.filter((c) => !isRegistered(c));

  const card = (cap: TenantDetail['capabilities'][number]): VNode =>
    CapabilityCard({
      cap,
      tenantId: t.tenantId,
      ownership: t.policy.ownership,
      view: t.views.find((v) => v.id === cap.view),
      previewBase: t.urls.preview,
      state,
      actions,
      now,
      issues: t.issues[cap.name] ?? [],
      health: healthByTool.get(cap.name),
      calls: callsByTool.get(cap.name)?.calls ?? 0,
      ms: callsByTool.get(cap.name)?.ms ?? 0,
    });

  return html`
    <div class="tabpanel">
      ${t.capabilities.length
        ? html`
            ${off.length
              ? html`<p class="sec__p">
                  ${served.length} of this site's ${t.capabilities.length} tools${SP}
                  ${served.length === 1 ? 'is' : 'are'} switched on and answering.${SP}
                  ${off.length} ${off.length === 1 ? 'is' : 'are'} in the manifest with both
                  surfaces off, below the second heading.
                </p>`
              : null}
            ${served.length
              ? html`<ul class="cap2list">
                  ${served.map(card)}
                </ul>`
              : Empty({
                  title: 'Every tool on this site is switched off.',
                  body: 'The manifest carries them and neither surface registers them, so the MCP endpoint answers tools/list with nothing. Each card below says what the crawl scored it, and the switch is on the card.',
                })}
            ${off.length
              ? html`
                  <h2 class="sec__h" id="tools-off-h">
                    Switched off${SP}<span class="gate__n"
                      >${off.length} in the manifest, not served</span
                    >
                  </h2>
                  <p class="sec__p">
                    The crawl verified these against the origin and then scored them as the site's
                    own furniture rather than something an agent would want — an index of the site's
                    news, a list of pages, a vocabulary that exists to fill another tool's
                    parameter. That judgement is a heuristic, and heuristics are wrong sometimes,
                    which is why the tool is here rather than deleted. Turn one on with the
                    <b>WebMCP page</b> and <b>MCP Apps</b> switches on its card; it appears on the
                    next <code>tools/list</code>.
                  </p>
                  <ul class="cap2list cap2list--off" aria-labelledby="tools-off-h">
                    ${off.map(card)}
                  </ul>
                `
              : null}
          `
        : Empty({
            title: 'This site has no tools.',
            body: 'Its manifest declares none, so it serves a branded page and an MCP endpoint with nothing on it. Tools come from a crawl: run one against the same origin and publish over this tenant.',
            action: { label: 'Crawl this site again', href: '#/crawl' },
          })}
    </div>
  `;
}
